import React, { useCallback, useEffect, useRef, useState } from "react";
import SectionMedia from "./SectionMedia";

// One large stage + mode-name pills instead of a cramped thumbnail grid.
// Auto-advances every `interval` ms; pauses on hover/focus; arrows and pills
// for manual control. Skips autoplay when the user prefers reduced motion.
export default function ModeCarousel({ modes, interval = 5000 }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useRef(false);

  useEffect(() => {
    reducedMotion.current =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    if (paused || reducedMotion.current || modes.length < 2) return;
    const id = setInterval(
      () => setActive((i) => (i + 1) % modes.length),
      interval
    );
    return () => clearInterval(id);
  }, [paused, modes.length, interval]);

  const go = useCallback(
    (i) => setActive(((i % modes.length) + modes.length) % modes.length),
    [modes.length]
  );

  const mode = modes[active];
  // Keep only the active slide and its neighbours as live videos; the rest
  // show their poster stills so six loops never download at once.
  const isNear = (i) => {
    const n = modes.length;
    return i === active || i === (active + 1) % n || i === (active + n - 1) % n;
  };

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label="Blender extension modes"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="mx-auto max-w-4xl"
    >
      <div className="relative aspect-[1280/936] overflow-hidden rounded-2xl bg-black shadow-2xl ring-1 ring-white/10">
        {modes.map((m, i) => (
          <div
            key={m.title}
            aria-hidden={i !== active}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
          >
            {isNear(i) ? (
              <SectionMedia
                src={m.media}
                alt={m.alt}
                poster={m.poster}
                className="h-full w-full object-contain"
              />
            ) : (
              <img
                src={m.poster || m.media}
                alt={m.alt}
                loading="lazy"
                className="h-full w-full object-contain"
              />
            )}
          </div>
        ))}

        {/* Caption overlay */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-6 pb-5 pt-20 sm:px-8">
          <h3 className="text-xl font-semibold text-white sm:text-2xl">
            {mode.title}
          </h3>
          <p className="mt-1 text-sm/6 text-gray-200 sm:text-base/7">
            {mode.text}
          </p>
        </div>

        {/* Prev / next arrows */}
        <button
          type="button"
          onClick={() => go(active - 1)}
          aria-label="Previous mode"
          className="absolute left-3 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-brand-pink/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-pink"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true" className="size-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => go(active + 1)}
          aria-label="Next mode"
          className="absolute right-3 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-brand-pink/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-pink"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true" className="size-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Mode pills */}
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        {modes.map((m, i) => (
          <button
            key={m.title}
            type="button"
            onClick={() => go(i)}
            aria-pressed={i === active}
            className={`rounded-full px-4 py-1.5 text-sm/6 font-semibold transition ${
              i === active
                ? "bg-brand-pink text-white shadow-sm"
                : "bg-white/5 text-brand-lightpurple ring-1 ring-inset ring-white/10 hover:text-brand-pink hover:ring-brand-pink/40"
            }`}
          >
            {m.title}
          </button>
        ))}
      </div>
    </div>
  );
}
